import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useQuery } from '@apollo/client';

import { CenteredLoader, TagListFromTagHighlights } from '@playbookux/playbook-web-core';
import VideoPlayer, { withMedia } from '../../../../../../core/components/video-player';
import AspectRatioContainer from '../../../../../../core/components/aspect-ratio-container';
import HangTight from '../../../../../../core/components/hang-tight';
import ControlBarSpacer from '../../../../../../core/components/video-player/control-bar-spacer';

import { TEST_SESSION_NOTE } from './graphql';
import {
  Modal,
  Container,
  Text,
  Title,
  ContainerText,
  Button,
  ContainerBottom,
  ContainerButton,
  ContainerTagHighlight,
  ErrorMessage,
  ContainerMessage,
  EditLink,
} from './styles';

const ERROR_MESSAGE = "We're not able to process this clip. Please contact support.";
const HANG_TIGHT_MESSAGE = 'Hang tight! We’re generating your clip';
const POLL_INTERVAL = 3000;

const ClipModal = (props) => {
  const {
    open,
    onClose,
    noteId,
    onAddClip,
    clipsMap,
  } = props;

  const [url, setUrl] = useState(null);

  const {
    loading,
    data = {},
    stopPolling,
    error,
  } = useQuery(TEST_SESSION_NOTE, {
    variables: {
      id: noteId,
    },
    pollInterval: POLL_INTERVAL,
    skip: !noteId,
  });

  const { testSessionNote } = data;

  useEffect(() => {
    if (!loading && testSessionNote && testSessionNote.status !== 'processing') {
      stopPolling();
      if (!url || url.split('?')[0] !== testSessionNote.videoUrl.split('?')[0]) {
        setUrl(testSessionNote.videoUrl);
      }
    }
  }, [testSessionNote, stopPolling, loading]);

  useEffect(() => {
    if (!open) {
      stopPolling();
      setUrl(null);
    }
  }, [open, stopPolling]);

  const handleClick = () => {
    onAddClip(testSessionNote);
    onClose();
  };

  const editClipUrl = useMemo(() => {
    if (testSessionNote) {
      const projectId = testSessionNote.testSession.tester.project.id;
      const testerId = testSessionNote.testSession.tester.id;

      return `/projects/${projectId}/videos/${testerId}?note=${noteId}`;
    }
    return '';
  }, [testSessionNote]);

  const showVideoPlayer = testSessionNote && testSessionNote.status === 'processed';
  const processingError = testSessionNote && testSessionNote.status === 'error';
  const showBlackComponent = !showVideoPlayer || processingError;

  const showButton = _.isEmpty(clipsMap[noteId]);

  const { tagHighlights = [] } = testSessionNote || {};

  return (
    <Modal
      open={open}
      closeOnEscape
      closeOnDimmerClick
      onClose={onClose}
    >
      {!loading && error && (
        <Container>
          <AspectRatioContainer
            ratio={16 / 9}
          >
            <ContainerMessage>
              <ErrorMessage>Error while retrieving the clip.</ErrorMessage>
            </ContainerMessage>
          </AspectRatioContainer>
        </Container>
      )}
      {!_.isEmpty(testSessionNote) && (
        <>
          <Container>
            <AspectRatioContainer
              ratio={16 / 9}
            >
              <>
                {loading && (
                  <CenteredLoader />
                )}
                {showBlackComponent && !loading && (
                  <HangTight
                    processingError={processingError}
                    hangTightMessage={HANG_TIGHT_MESSAGE}
                    errorMessage={ERROR_MESSAGE}
                  />
                )}
                {showVideoPlayer && !loading && url && (
                  <VideoPlayer src={url}>
                    <ControlBarSpacer
                      beforeElement="downloadButton"
                    />
                  </VideoPlayer>
                )}
              </>
            </AspectRatioContainer>
          </Container>
          <ContainerBottom>
            <ContainerText>
              <Title>
                {`Clip from ${testSessionNote.testSession.tester.project.name}`}
                {' - '}
                <EditLink
                  target="_blank"
                  href={editClipUrl}
                >
                  Edit Clip
                </EditLink>
              </Title>
              <Text>{testSessionNote.note}</Text>

            </ContainerText>
            {showButton && (
              <ContainerButton>
                <Button onClick={handleClick}>
                  Add to Highlight Reel
                </Button>
              </ContainerButton>
            )}
          </ContainerBottom>

          <ContainerTagHighlight>
            <TagListFromTagHighlights
              tagHighlights={tagHighlights}
            />
          </ContainerTagHighlight>

        </>
      )}
    </Modal>
  );
};

ClipModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAddClip: PropTypes.func.isRequired,
  noteId: PropTypes.string,
  clipsMap: PropTypes.array.isRequired,
};

ClipModal.defaultProps = {
  open: false,
  noteId: null,
};

export default withMedia(ClipModal);
